import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { FinancialStatementPeriod } from 'common/types/financials/financials';

declare global {
  interface Window {
    appGlobal?: FinancialsContextProps;
  }
}
interface FinancialsContextProps {
  periods?: FinancialStatementPeriod[];
  incomeStatement?: object;
  setIncomeStatement?: Dispatch<SetStateAction<object>>;
  balanceSheets?: object;
  setBalanceSheets?: Dispatch<SetStateAction<object>>;
  kpi?: object;
  setKpi?: Dispatch<SetStateAction<object>>;
  useAdjustedEBITDA?: boolean;
  setUseAdjustedEBITDA?: Dispatch<SetStateAction<boolean | undefined>>;

  children?: ReactNode;
}

export const FinancialsContext = createContext<FinancialsContextProps>({} as FinancialsContextProps);

const FinancialsProvider = ({
  children,
  setIncomeStatement,
  setBalanceSheets,
  setKpi,
  ...props
}: FinancialsContextProps) => {
  useEffect(
    () =>
      // auto formatting will try to consolidate this into nested anon functions - this comment prevents that
      () => {
        if (window.appGlobal) {
          delete window.appGlobal.incomeStatement;
        }
      },
    []
  );

  const setIncomeStatementData = useCallback(
    (data: any) => {
      window.appGlobal = {
        ...window.appGlobal,
        incomeStatement: {
          ...window.appGlobal?.incomeStatement,
          ...data,
        },
      };

      setIncomeStatement?.(data);
    },
    [setIncomeStatement]
  );

  const setBalanceSheetsData = useCallback(
    (data: any) => {
      window.appGlobal = {
        ...window.appGlobal,
        balanceSheets: {
          ...window.appGlobal?.balanceSheets,
          ...data,
        },
      };

      setBalanceSheets?.(data);
    },
    [setBalanceSheets]
  );

  const setKpiData = useCallback(
    (data: any) => {
      window.appGlobal = {
        ...window.appGlobal,
        kpi: {
          ...window.appGlobal?.kpi,
          ...data,
        },
      };

      setKpi?.(data);
    },
    [setKpi]
  );

  const providerValue = useMemo(
    () => ({
      ...props,
      setIncomeStatement,
      setIncomeStatementData,
      setBalanceSheets,
      setBalanceSheetsData,
      setKpi,
      setKpiData,
    }),
    [props, setIncomeStatement, setIncomeStatementData, setBalanceSheets, setBalanceSheetsData, setKpi, setKpiData]
  );

  return <FinancialsContext.Provider value={providerValue}>{children}</FinancialsContext.Provider>;
};

const useFinancials = () => useContext(FinancialsContext);

export { FinancialsProvider, useFinancials };
